/**
 * O destino de volta do login (Story 25.1) — e a única porta por onde ele passa.
 *
 * Quem abre um link interno sem sessão vai para `/sign-in?next=<caminho+query>`, e
 * o login devolve-o a esse caminho. O destino mora no URL, e não no estado do
 * router, para sobreviver a um reload da tela de login.
 *
 * ⚠️ E é justamente por morar no URL que ele nunca é usado cru: o URL é de quem o
 * escreveu. Um `…/sign-in?next=https://evil.com` mandado por email entregaria a
 * pessoa, já autenticada, a outro sítio (open redirect). O `safeNext` só aceita um
 * caminho interno do lado certo da fronteira do papel; em qualquer outro caso
 * devolve a casa do papel (`homeForRole`), que é o que o login fazia antes de o
 * destino existir.
 *
 * Função pura, sem React e sem `window`: o sign-in fresco (`hooks/use-auth.ts`) e o
 * sign-in com sessão viva (`routes/sign-in.tsx`) pedem a MESMA resposta, e duas
 * opiniões sobre o que é um destino seguro é como uma delas fica para trás.
 */
import type { UserRole } from '@/lib/api/me'
import { PORTAL_HOME, homeForRole } from '@/lib/roles'

/**
 * As telas que existem para CRIAR uma sessão. Voltar a elas depois de entrar não
 * leva a lado nenhum — no caso do `/sign-in?next=…`, leva de volta ao próprio
 * login. São as quatro públicas de autenticação do `App.tsx`; o `/catalog/:token`
 * é pública mas não é de autenticação, e nunca aparece como destino porque não
 * passa pelas guardas.
 */
const AUTH_SCREENS = ['/sign-in', '/accept-invite', '/reset-password', '/forgot-password'] as const

/**
 * A API (Story 35.4, Epic 35 §9). Não é tela: o login com senha entra por
 * `window.location.href`, uma navegação de verdade, e um destino aqui abriria JSON —
 * ou, no `/api/gms-auth/login`, recomeçaria o login da Genesis sem ninguém o pedir.
 */
const API_ROOT = '/api'

/**
 * Uma origem qualquer, só para o `URL` ter contra o que resolver um caminho. O
 * `.invalid` é reservado (RFC 2606): nunca resolve, e ninguém o confunde com um
 * endereço do produto.
 */
const PARSE_BASE = 'https://caliber.invalid'

/**
 * O caminho que o NAVEGADOR vai abrir e que o React Router vai casar, para
 * classificar o destino — e não o texto cru. Três passos, cada um de quem decide:
 *
 *   • o `URL` (o navegador) desfaz os `..`, também os codificados (`%2e%2e`): é
 *     por isso que `/portal/../dashboard` conta como `/dashboard`;
 *   • a decodificação por trecho é a do router (`decodePath` do React Router 7):
 *     cada trecho passa por `decodeURIComponent`, e um `/` que saia de um `%2F`
 *     volta a `%2F`, porque não separa trechos. Sem ela, `/%73ign-in` passava por
 *     outra coisa que não o login, e é o login que o router abre (QA, SEC-001);
 *   • as minúsculas também são do router, que casa rotas sem ligar a maiúsculas
 *     (`/PORTAL` abre o portal).
 *
 * Um `%` malformado devolve `null`, e o destino é recusado. O router, nesse caso,
 * casa o caminho inteiro sem decodificar; aqui recusa-se, que é o lado seguro da
 * dúvida.
 *
 * ⚠️ Só serve para CLASSIFICAR. O que se devolve é o `next` cru: o caminho
 * normalizado de `/.//evil.com` é `//evil.com`, e devolver esse texto seria
 * fabricar o open redirect que o resto da função recusa.
 */
function pathOf(target: string): string | null {
  const pathname = new URL(target, PARSE_BASE).pathname
  try {
    return pathname
      .split('/')
      .map((segment) => decodeURIComponent(segment).replace(/\//g, '%2F'))
      .join('/')
      .toLowerCase()
  } catch {
    return null
  }
}

/** `root` ou qualquer coisa debaixo dele — e não um prefixo de texto: `/portalx` não é `/portal`. */
function isUnder(path: string, root: string): boolean {
  return path === root || path.startsWith(`${root}/`)
}

/**
 * O destino aceito, ou a casa do papel. Ver o cabeçalho do módulo.
 *
 * As regras, por ordem — cada uma tem teste próprio em
 * `lib/__tests__/safe-next.test.ts`, e a prova por mutação da story retira-as uma
 * a uma:
 *
 *   1. vazio → casa;
 *   2. bytes de controlo → casa (o navegador APAGA `\t`, `\n` e `\r` do meio de um
 *      URL: `/\t/evil.com` chegaria lá como `//evil.com`);
 *   3. esquema → casa: um caminho interno começa com `/`, e `https://…`,
 *      `javascript:…` e `evil.com` não;
 *   4. `//` → casa: é um endereço de outro host sem esquema (protocol-relative);
 *   5. `/\` → casa: o navegador troca `\` por `/` num URL http(s), e lê `//`;
 *   6. `%` malformado no caminho → casa (ver `pathOf`);
 *   7. caminho que o navegador abre como `//…` → casa: `/.//evil.com` fica na
 *      mesma origem, mas abre `https://<caliber>//evil.com`, e nenhuma tela mora
 *      lá (QA, INFO-001);
 *   8. telas de autenticação → casa;
 *   9. rotas da API (`/api…`) → casa;
 *  10. o papel: o cliente só volta a `/portal…`, a Genesis nunca volta a ele.
 *
 * Papel desconhecido conta como Genesis, pela mesma razão do `homeForRole`.
 */
export function safeNext(
  next: string | null | undefined,
  role: UserRole | null | undefined,
): string {
  const home = homeForRole(role)

  if (!next) return home
  if (/\p{Cc}/u.test(next)) return home
  if (!next.startsWith('/')) return home
  if (next.startsWith('//')) return home
  if (next.startsWith('/\\')) return home

  const path = pathOf(next)
  if (path === null) return home
  if (path.startsWith('//')) return home
  if (AUTH_SCREENS.some((screen) => isUnder(path, screen))) return home
  if (isUnder(path, API_ROOT)) return home
  if (isUnder(path, PORTAL_HOME) !== (role === 'customer')) return home

  return next
}

/**
 * O endereço do login que devolve a pessoa a `location` depois de entrar:
 * `/sign-in?next=<caminho+query>`, com o destino codificado por `URLSearchParams`.
 *
 * Sem o fragmento (`#…`): nenhuma tela do produto o lê, e o PRD fala de caminho +
 * query. As guardas são quem chama: é lá que se sabe que a pessoa queria estar
 * aqui e não pôde.
 */
export function signInPathFor(location: { pathname: string; search: string }): string {
  const params = new URLSearchParams({ next: `${location.pathname}${location.search}` })
  return `/sign-in?${params.toString()}`
}
